/**
 * Aspect Admin — minimal executive SVG charts (no dependencies).
 */

function pathFrom(points, w, h, pad, min, max) {
  const n = points.length;
  const x = (i) => pad + (i / (n - 1)) * (w - pad * 2);
  const y = (v) => h - pad - ((v - min) / (max - min || 1)) * (h - pad * 2);
  return points.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
}

/** Smooth catmull-rom-ish path for premium curves. */
function smoothPath(points, w, h, pad, min, max) {
  const n = points.length;
  const px = (i) => pad + (i / (n - 1)) * (w - pad * 2);
  const py = (v) => h - pad - ((v - min) / (max - min || 1)) * (h - pad * 2);
  const pts = points.map((v, i) => [px(i), py(v)]);
  if (pts.length < 3) return pathFrom(points, w, h, pad, min, max);
  let d = `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    d += ` C${c1[0].toFixed(1)},${c1[1].toFixed(1)} ${c2[0].toFixed(1)},${c2[1].toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)}`;
  }
  return d;
}

/** Tiny sparkline for KPI cards. */
export function sparkline(points, { w = 92, h = 30, color = "#00a8d2" } = {}) {
  const min = Math.min(...points);
  const max = Math.max(...points);
  const line = smoothPath(points, w, h, 2, min, max);
  const area = `${line} L${w - 2},${h - 1} L2,${h - 1} Z`;
  const gid = `sg${Math.random().toString(36).slice(2, 7)}`;
  return `
    <svg class="ap-chart" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" aria-hidden="true">
      <defs>
        <linearGradient id="${gid}" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="${color}" stop-opacity="0.22"/>
          <stop offset="100%" stop-color="${color}" stop-opacity="0"/>
        </linearGradient>
      </defs>
      <path d="${area}" fill="url(#${gid})"/>
      <path d="${line}" fill="none" stroke="${color}" stroke-width="1.8" stroke-linecap="round"/>
    </svg>
  `;
}

/**
 * Multi-series smooth area/line trend chart with grid + labels.
 * Padding is configurable so the chart reads well at any height.
 */
export function trendChart(
  { labels, series },
  { w = 720, h = 240, padL = 38, padR = 16, padT = 16, padB = 26, gridLines = 4 } = {}
) {
  const all = series.flatMap((s) => s.points);
  const min = 0;
  const max = Math.max(...all) * 1.12;
  const plotW = w - padL - padR;
  const plotH = h - padT - padB;
  const uid = Math.random().toString(36).slice(2, 7);

  const px = (i) => padL + (i / (labels.length - 1)) * plotW;
  const py = (v) => padT + (1 - (v - min) / (max - min || 1)) * plotH;

  const grid = Array.from({ length: gridLines + 1 }, (_, i) => {
    const y = padT + (i / gridLines) * plotH;
    const val = Math.round(max - (i / gridLines) * (max - min));
    const label = val >= 1000 ? `${(val / 1000).toFixed(val >= 10000 ? 0 : 1)}k` : val;
    return `
      <line class="ap-chart__grid" x1="${padL}" y1="${y.toFixed(1)}" x2="${w - padR}" y2="${y.toFixed(1)}"/>
      <text class="ap-chart__ylabel" x="${padL - 9}" y="${(y + 3.5).toFixed(1)}" text-anchor="end">${label}</text>
    `;
  }).join("");

  const xLabels = labels
    .map((l, i) => `<text x="${px(i).toFixed(1)}" y="${h - 7}" text-anchor="middle">${l}</text>`)
    .join("");

  const paths = series
    .map((s, si) => {
      const line = smoothPathXY(s.points, px, py);
      const area = `${line} L${px(s.points.length - 1).toFixed(1)},${(h - padB).toFixed(1)} L${padL.toFixed(1)},${(h - padB).toFixed(1)} Z`;
      const last = s.points[s.points.length - 1];
      const lx = px(s.points.length - 1);
      const ly = py(last);
      const gradId = `tg${uid}-${si}`;
      return `
        <defs>
          <linearGradient id="${gradId}" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="${s.color}" stop-opacity="${si === 0 ? 0.2 : 0.08}"/>
            <stop offset="100%" stop-color="${s.color}" stop-opacity="0"/>
          </linearGradient>
        </defs>
        <path d="${area}" fill="url(#${gradId})"/>
        <path d="${line}" fill="none" stroke="${s.color}" stroke-width="${si === 0 ? 2.4 : 1.8}" stroke-linecap="round" stroke-linejoin="round" opacity="${si === 0 ? 1 : 0.75}"/>
        <circle cx="${lx.toFixed(1)}" cy="${ly.toFixed(1)}" r="3.6" fill="#fff" stroke="${s.color}" stroke-width="2.2"/>
        <circle cx="${lx.toFixed(1)}" cy="${ly.toFixed(1)}" r="6.5" fill="${s.color}" opacity="0.12"/>
      `;
    })
    .join("");

  return `
    <svg class="ap-chart ap-chart--trend" viewBox="0 0 ${w} ${h}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Trend chart">
      ${grid}
      ${paths}
      ${xLabels}
    </svg>
  `;
}

/** Smooth catmull-rom path using explicit x/y projector functions. */
function smoothPathXY(points, px, py) {
  const pts = points.map((v, i) => [px(i), py(v)]);
  if (pts.length < 3) return pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  let d = `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    d += ` C${c1[0].toFixed(1)},${c1[1].toFixed(1)} ${c2[0].toFixed(1)},${c2[1].toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)}`;
  }
  return d;
}

/** Donut chart with center label. */
export function donutChart(slices, { size = 168, stroke = 17, centerLabel = "", centerSub = "" } = {}) {
  const r = (size - stroke) / 2;
  const c = size / 2;
  const circ = 2 * Math.PI * r;
  const total = slices.reduce((s, x) => s + x.value, 0);
  let offset = 0;
  const rings = slices
    .map((s) => {
      const frac = s.value / total;
      const dash = `${(frac * circ - 2).toFixed(2)} ${(circ - frac * circ + 2).toFixed(2)}`;
      const el = `<circle cx="${c}" cy="${c}" r="${r}" fill="none" stroke="${s.color}" stroke-width="${stroke}" stroke-dasharray="${dash}" stroke-dashoffset="${(-offset * circ + circ / 4).toFixed(2)}" stroke-linecap="round"/>`;
      offset += frac;
      return el;
    })
    .join("");
  return `
    <svg class="ap-chart" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" role="img" aria-label="Distribution chart">
      <circle cx="${c}" cy="${c}" r="${r}" fill="none" stroke="rgba(13,41,58,0.05)" stroke-width="${stroke}"/>
      ${rings}
      <text x="${c}" y="${c - 2}" text-anchor="middle" style="font-size:20px;font-weight:700;fill:var(--ap-ink);">${centerLabel}</text>
      <text x="${c}" y="${c + 16}" text-anchor="middle">${centerSub}</text>
    </svg>
  `;
}

/** Horizontal bar list (e.g. top spaces). */
export function barRow(pct, color = "#00a8d2") {
  return `
    <div class="ap-meter__track" style="height:6px;">
      <div class="ap-meter__fill" style="width:${pct}%;background:linear-gradient(90deg,${color},${color}cc);box-shadow:none;"></div>
    </div>
  `;
}

export function legend(items) {
  return `
    <div class="ap-legend">
      ${items.map((s) => `<span><span class="ap-legend__swatch" style="background:${s.color}"></span>${s.label ?? s.name}</span>`).join("")}
    </div>
  `;
}
